import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Loader2 } from "lucide-react";

const SERVICES = [
  { value: "matrimonio", label: "Matrimonio / Wedding Premium" },
  { value: "compleanno", label: "Compleanno & Cerimonie" },
  { value: "aziendale", label: "Foto & Video Aziendali" },
  { value: "consulenza", label: "Consulenza gratuita" },
  { value: "altro", label: "Altro" },
];

export const LeadForm = ({ defaultService = "consulenza", title = "Richiedi Preventivo", subtitle }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service_type: defaultService,
    event_date: "", location: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Inserisci nome ed email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      trackEvent("generate_lead", { service_type: form.service_type });
      toast.success("Richiesta inviata! Ti ricontatterò al più presto.");
      setForm({ name: "", email: "", phone: "", service_type: defaultService, event_date: "", location: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-stone-900/60 border-stone-800 text-white placeholder:text-stone-500 focus:ring-2 focus:ring-[#d4af37] focus:ring-offset-2 focus:ring-offset-[#0c0c0c] rounded-sm h-12";

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-5">
      {title && <h3 className="font-serif text-2xl text-white">{title}</h3>}
      {subtitle && <p className="text-stone-400 text-sm -mt-2">{subtitle}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-stone-400">Nome *</Label>
          <Input data-testid="lead-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Il tuo nome" className={inputCls} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-stone-400">Email *</Label>
          <Input data-testid="lead-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="nome@email.it" className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-stone-400">Telefono</Label>
          <Input data-testid="lead-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Telefono / WhatsApp" className={inputCls} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-stone-400">Tipo di servizio</Label>
          <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
            <SelectTrigger data-testid="lead-service" className={inputCls}>
              <SelectValue placeholder="Seleziona" />
            </SelectTrigger>
            <SelectContent className="bg-stone-900 border-stone-800 text-white">
              {SERVICES.map((s) => (
                <SelectItem key={s.value} value={s.value} className="focus:bg-stone-800 focus:text-gold">
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-stone-400">Data evento</Label>
          <Input data-testid="lead-date" type="date" value={form.event_date} onChange={(e) => update("event_date", e.target.value)} className={inputCls} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-stone-400">Località</Label>
          <Input data-testid="lead-location" value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Es. Viterbo, Roma" className={inputCls} />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest text-stone-400">Messaggio</Label>
        <Textarea data-testid="lead-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Raccontami del tuo evento..." rows={4} className="bg-stone-900/60 border-stone-800 text-white placeholder:text-stone-500 focus:ring-2 focus:ring-[#d4af37] focus:ring-offset-2 focus:ring-offset-[#0c0c0c] rounded-sm" />
      </div>

      <button
        type="submit"
        data-testid="lead-submit"
        disabled={loading}
        className="w-full bg-gold text-[#0c0c0c] uppercase tracking-[0.15em] text-sm font-medium py-4 hover:bg-[#b5952f] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading && <Loader2 size={18} className="animate-spin" />}
        {loading ? "Invio in corso..." : "Invia Richiesta"}
      </button>
    </form>
  );
};

export default LeadForm;
